
import { useEffect, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { useLeadDistributionConfig } from './useLeadDistributionConfig';
import { leadVeioDeAnuncio } from '@/lib/leadVeioDeAnuncio';

/**
 * Hook para gerenciar leads no Supabase
 *
 * Este hook gerencia todas as operações CRUD relacionadas aos leads:
 * - Buscar leads da clínica do usuário autenticado
 * - Criar novos leads
 * - Atualizar leads existentes
 * - Deletar leads
 * - Controlar follow-up automático (NOVO)
 *
 * Com as políticas RLS implementadas, os leads são automaticamente
 * filtrados pela clínica do usuário logado, garantindo isolamento de dados.
 *
 * NOVO: Campos de controle de follow-up:
 * - follow_up_pausado: controla se o lead deve receber follow-ups automáticos
 * - data_ultimo_followup: registra quando foi enviado o último follow-up
 */

export interface Lead {
  id: string;
  // CORREÇÃO: O nome agora pode ser nulo para acomodar leads de fontes como o Instagram
  nome: string | null;
  telefone: string | null;
  email: string | null;
  etapa_kanban_id: string | null;
  tag_id: string | null;
  anotacoes: string | null;
  origem_lead: string | null;
  servico_interesse: string | null;
  status_conversao: string | null;
  convertido: boolean | null;
  ltv: number | null;
  data_ultimo_contato: string | null;
  clinica_id: string | null;
  created_at: string | null;
  updated_at: string | null;
  // NOVOS CAMPOS para follow-up
  follow_up_pausado: boolean | null;
  data_ultimo_followup: string | null;
  ai_conversation_enabled: boolean | null;
  // CAMPO para rastrear tempo na etapa (atualizado via trigger no banco)
  etapa_changed_at: string | null;
  // CAMPOS ADICIONAIS para compatibilidade com componentes
  etapa_id: string | null; // Alias para etapa_kanban_id
  avatar_url: string | null; // Avatar do lead
  nome_clinica: string | null; // Nome da clínica
  ad_name: string | null; // Nome do anúncio específico
}

export interface LeadSearchOption {
  id: string;
  nome: string | null;
  telefone: string | null;
  origem_lead?: string | null;
}

export interface CreateLeadData {
  // CORREÇÃO: O nome agora é opcional na criação do lead
  nome?: string;
  telefone?: string;
  email?: string;
  etapa_kanban_id?: string;
  tag_id?: string;
  anotacoes?: string;
  origem_lead?: string;
  servico_interesse?: string;
  clinica_id: string; // Obrigatório para associar à clínica correta
  // NOVOS CAMPOS opcionais para follow-up
  follow_up_pausado?: boolean;
  ai_conversation_enabled?: boolean;
  ad_name?: string; // Nome do anúncio
  anuncio?: string | null;
}

export interface UpdateLeadData extends Partial<CreateLeadData> {
  id: string;
  data_ultimo_followup?: string; // NOVO: permite atualizar data do último follow-up
}

interface UseLeadsOptions {
  enabled?: boolean;
}

// Hook para buscar todos os leads da clínica do usuário
export const useLeads = ({ enabled = true }: UseLeadsOptions = {}) => {
  return useQuery({
    queryKey: ['leads'],
    queryFn: async (): Promise<Lead[]> => {
      console.log('Buscando leads da clínica do usuário...');

      // Tabela: leads; Operação: SELECT; Campos: todos os campos usados pelas telas que reutilizam este hook.
      // Filtro: as políticas RLS restringem o resultado à clínica do usuário autenticado.
      // Motivo: o hook é compartilhado; telas que só precisam dos leads ao abrir um modal podem desabilitá-lo até esse momento.
      const { data, error } = await supabase
        .from('leads')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Erro ao buscar leads:', error);
        throw new Error(`Erro ao buscar leads: ${error.message}`);
      }

      console.log(`${data?.length || 0} leads encontrados`);

      // Mapear dados para incluir campos de compatibilidade
      return (data || []).map((lead: any) => ({
        ...lead,
        etapa_id: lead.etapa_kanban_id, // Alias para compatibilidade
        avatar_url: lead.avatar_url || null,
        nome_clinica: lead.nome_clinica || null,
        ad_name: lead.ad_name || null
      }));
    },
    // Evita baixar a base inteira em componentes montados apenas como modal fechado.
    enabled,
    staleTime: 30000, // Cache por 30 segundos
  });
};

/**
 * Busca leve para o seletor da Agenda.
 * Consulta apenas a clínica ativa, devolve no máximo 10 opções e preserva a RLS da tabela leads.
 */
export const useAgendaLeadSearch = (
  clinicaId: string | undefined,
  termoDigitado: string,
  clienteSelecionadoId?: string,
  enabled = true,
) => {
  const [termoDebounced, setTermoDebounced] = useState('');

  useEffect(() => {
    // Evita uma chamada ao Supabase a cada tecla sem deixar a interface parecer lenta.
    const temporizador = window.setTimeout(() => setTermoDebounced(termoDigitado.trim()), 250);
    return () => window.clearTimeout(temporizador);
  }, [termoDigitado]);

  return useQuery({
    queryKey: ['leads', 'agenda-search', clinicaId, termoDebounced, clienteSelecionadoId ?? null],
    enabled: enabled && Boolean(clinicaId),
    placeholderData: dadosAnteriores => dadosAnteriores,
    staleTime: 60 * 1000,
    queryFn: async (): Promise<LeadSearchOption[]> => {
      if (!clinicaId) return [];

      // Uma única letra gera resultados pouco úteis; aguarda a segunda antes de consultar o banco.
      if (termoDebounced.length === 1) return [];

      // Remove caracteres de controle do filtro PostgREST; percentuais são adicionados apenas pela aplicação.
      const termoSeguro = termoDebounced.replace(/[,()%_*]/g, ' ').replace(/\s+/g, ' ').trim();
      const telefoneSomenteNumeros = termoDebounced.replace(/\D/g, '');
      const buscaPorTelefone = telefoneSomenteNumeros.length >= 2 &&
        termoDebounced.replace(/[\d\s()+-]/g, '').length === 0;

      // Tabela: leads | Operação: SELECT | Campos: id, nome, telefone e origem_lead.
      // Filtros: clinica_id da agenda e nome/telefone digitado; limite: 10 contatos.
      // origem_lead entra só para o seletor de disparo reconhecer contato do Instagram.
      // Risco: a RLS continua sendo aplicada e nenhum campo comercial ou sensível adicional é transferido.
      let consulta = supabase
        .from('leads')
        .select('id, nome, telefone, origem_lead')
        .eq('clinica_id', clinicaId)
        .order('created_at', { ascending: false })
        .limit(10);

      if (termoSeguro.length >= 2) {
        // Usa somente o índice adequado à entrada: nome para texto e telefone para números.
        consulta = consulta.ilike(
          buscaPorTelefone ? 'telefone' : 'nome',
          `%${buscaPorTelefone ? telefoneSomenteNumeros : termoSeguro}%`,
        );
      }

      const { data, error } = await consulta;
      if (error) throw new Error('Não foi possível buscar os contatos. Tente novamente.');

      const contatos = (data ?? []) as LeadSearchOption[];
      if (
        !clienteSelecionadoId ||
        termoDebounced.length >= 2 ||
        contatos.some(contato => contato.id === clienteSelecionadoId)
      ) {
        return contatos;
      }

      // Em edição, busca somente o cliente já vinculado para conseguir mostrar seu nome mesmo fora dos 10 recentes.
      const { data: clienteSelecionado, error: erroClienteSelecionado } = await supabase
        .from('leads')
        .select('id, nome, telefone, origem_lead')
        .eq('clinica_id', clinicaId)
        .eq('id', clienteSelecionadoId)
        .maybeSingle();

      if (erroClienteSelecionado) throw new Error('Não foi possível carregar o cliente do agendamento.');
      return clienteSelecionado ? [clienteSelecionado as LeadSearchOption, ...contatos].slice(0, 10) : contatos;
    },
  });
};

// Hook para buscar leads por etapa kanban
export const useLeadsByEtapa = (etapaId: string | null) => {
  return useQuery({
    queryKey: ['leads', 'etapa', etapaId],
    queryFn: async (): Promise<Lead[]> => {
      if (!etapaId) return [];

      console.log('Buscando leads da etapa:', etapaId);

      const { data, error } = await supabase
        .from('leads')
        .select('*')
        .eq('etapa_kanban_id', etapaId)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Erro ao buscar leads por etapa:', error);
        throw new Error(`Erro ao buscar leads: ${error.message}`);
      }

      // Mapear dados para incluir campos de compatibilidade
      return (data || []).map((lead: any) => ({
        ...lead,
        etapa_id: lead.etapa_kanban_id,
        avatar_url: lead.avatar_url || null,
        nome_clinica: lead.nome_clinica || null,
        ad_name: lead.ad_name || null
      }));
    },
    enabled: !!etapaId,
    staleTime: 30000,
  });
};

// Hook para criar novo lead
export const useCreateLead = () => {
  const queryClient = useQueryClient();
  const { config: distributionConfig } = useLeadDistributionConfig();

  return useMutation({
    mutationFn: async (leadData: CreateLeadData): Promise<Lead> => {
      console.log('Criando novo lead:', leadData);

      // Aplicar distribuição automática se estiver ativada e etapa não for especificada
      let finalLeadData = { ...leadData };
      if (
        !leadData.etapa_kanban_id &&
        distributionConfig?.auto_lead_distribution_enabled &&
        distributionConfig?.default_etapa_id &&
        (
          !distributionConfig.auto_lead_distribution_ads_only
          || leadVeioDeAnuncio(leadData)
        )
      ) {
        finalLeadData.etapa_kanban_id = distributionConfig.default_etapa_id;
        console.log('✨ Aplicando distribuição automática para etapa:', distributionConfig.default_etapa_id);
      }

      const { data, error } = await supabase
        .from('leads')
        .insert([finalLeadData])
        .select()
        .single();

      if (error) {
        console.error('Erro ao criar lead:', error);
        throw new Error(`Erro ao criar lead: ${error.message}`);
      }

      console.log('Lead criado com sucesso:', data);

      // Mapear dados para incluir campos de compatibilidade
      return {
        ...data,
        etapa_id: data.etapa_kanban_id,
        avatar_url: data.avatar_url || null,
        nome_clinica: data.nome_clinica || null,
        ad_name: data.ad_name || null
      };
    },
    onSuccess: () => {
      // Invalidar cache dos leads para refletir a mudança
      queryClient.invalidateQueries({ queryKey: ['leads'] });
      toast.success('Lead criado com sucesso!');
    },
    onError: (error: Error) => {
      console.error('Erro na criação do lead:', error);
      toast.error(`Erro ao criar lead: ${error.message}`);
    },
  });
};

// Hook para atualizar lead existente
export const useUpdateLead = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (leadData: UpdateLeadData): Promise<Lead> => {
      const { id, ...updateData } = leadData;
      console.log('Atualizando lead:', id, updateData);

      // Adicionar timestamp de atualização
      const dataWithTimestamp = {
        ...updateData,
        updated_at: new Date().toISOString(),
      };

      const { data, error } = await supabase
        .from('leads')
        .update(dataWithTimestamp)
        .eq('id', id)
        .select()
        .single();

      if (error) {
        console.error('Erro ao atualizar lead:', error);
        throw new Error(`Erro ao atualizar lead: ${error.message}`);
      }

      console.log('Lead atualizado com sucesso:', data);

      // Mapear dados para incluir campos de compatibilidade
      return {
        ...data,
        etapa_id: data.etapa_kanban_id,
        avatar_url: data.avatar_url || null,
        nome_clinica: data.nome_clinica || null,
        ad_name: data.ad_name || null
      };
    },
    onMutate: async (leadData: UpdateLeadData) => {
      // Cancelar queries que podem sobreescrever nossa atualização otimista
      await queryClient.cancelQueries({ queryKey: ['leads'] });
      await queryClient.cancelQueries({ queryKey: ['chat-inbox'] });
      await queryClient.cancelQueries({ queryKey: ['pipeline-leads'] });
      await queryClient.cancelQueries({ queryKey: ['adminPipelineData'] });

      // Salvar os dados para rollback se der erro
      const previousLeads = queryClient.getQueryData(['leads']);

      // 1. Atualizar optimistamente os leads
      queryClient.setQueryData(['leads'], (old: any) => {
        if (!old) return old;
        return old.map((lead: any) =>
          lead.id === leadData.id ? { ...lead, ...leadData } : lead
        );
      });

      // 2. Atualizar optimistamente as conversas (scroll infinito) usando setQueriesData
      // pois a chave do inbox possui parâmetros dinâmicos de clínica, busca e filtro.
      queryClient.setQueriesData({ queryKey: ['chat-inbox'] }, (old: any) => {
        if (!old || !old.pages) return old;
        return {
          ...old,
          pages: old.pages.map((page: any) => ({
            ...page,
            conversations: page.conversations.map((conv: any) =>
              conv.id === leadData.id ? { ...conv, ...leadData } : conv
            )
          }))
        };
      });

      // 3. Atualizar optimistamente os dados do pipeline normal
      queryClient.setQueryData(['pipeline-leads'], (old: any) => {
        if (!old || !old.leads) return old;
        return {
          ...old,
          leads: old.leads.map((lead: any) =>
            lead.id === leadData.id ? { ...lead, ...leadData } : lead
          ),
          leadsOrfaos: old.leadsOrfaos.map((lead: any) =>
            lead.id === leadData.id ? { ...lead, ...leadData } : lead
          )
        };
      });

      // 4. Atualizar optimistamente os dados do pipeline admin (usando setQueriesData por ter clinicaId na chave)
      queryClient.setQueriesData({ queryKey: ['adminPipelineData'] }, (old: any) => {
        if (!old || !old.leads) return old;
        return {
          ...old,
          leads: old.leads.map((lead: any) =>
            lead.id === leadData.id ? { ...lead, ...leadData } : lead
          ),
          leadsOrfaos: old.leadsOrfaos.map((lead: any) =>
            lead.id === leadData.id ? { ...lead, ...leadData } : lead
          )
        };
      });

      return { previousLeads };
    },
    onSuccess: () => {
      // Invalidar cache dos leads e pipeline para refletir a mudança no server
      queryClient.invalidateQueries({ queryKey: ['leads'] });
      queryClient.invalidateQueries({ queryKey: ['etapas'] });
      queryClient.invalidateQueries({ queryKey: ['adminPipelineData'] });
      queryClient.invalidateQueries({ queryKey: ['chat-inbox'] });
      toast.success('Lead atualizado com sucesso!');
    },
    onError: (error: Error, variables, context: any) => {
      if (context?.previousLeads) {
        queryClient.setQueryData(['leads'], context.previousLeads);
      }
      console.error('Erro na atualização do lead:', error);
      toast.error(`Erro ao atualizar lead: ${error.message}`);
    },
  });
};

// Hook para deletar lead
export const useDeleteLead = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (leadId: string): Promise<void> => {
      console.log('Deletando lead:', leadId);

      const { error } = await supabase
        .from('leads')
        .delete()
        .eq('id', leadId);

      if (error) {
        console.error('Erro ao deletar lead:', error);
        throw new Error(`Erro ao deletar lead: ${error.message}`);
      }

      console.log('Lead deletado com sucesso');
    },
    onSuccess: () => {
      // Invalidar cache dos leads para refletir a mudança
      queryClient.invalidateQueries({ queryKey: ['leads'] });
      toast.success('Lead deletado com sucesso!');
    },
    onError: (error: Error) => {
      console.error('Erro na exclusão do lead:', error);
      toast.error(`Erro ao deletar lead: ${error.message}`);
    },
  });
};

// Hook para mover lead para outra etapa
export const useMoveLeadToEtapa = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ leadId, etapaId }: { leadId: string; etapaId: string }) => {
      console.log('Movendo lead para etapa:', leadId, etapaId);

      const { data, error } = await supabase
        .from('leads')
        .update({
          etapa_kanban_id: etapaId,
          updated_at: new Date().toISOString(),
        })
        .eq('id', leadId)
        .select()
        .single();

      if (error) {
        console.error('Erro ao mover lead:', error);
        throw new Error(`Erro ao mover lead: ${error.message}`);
      }

      // Mapear dados para incluir campos de compatibilidade
      return {
        ...data,
        etapa_id: data.etapa_kanban_id,
        avatar_url: data.avatar_url || null,
        nome_clinica: data.nome_clinica || null,
        ad_name: data.ad_name || null
      };
    },
    onSuccess: () => {
      // Invalidar cache dos leads para refletir a mudança
      queryClient.invalidateQueries({ queryKey: ['leads'] });
      toast.success('Lead movido com sucesso!');
    },
    onError: (error: Error) => {
      console.error('Erro ao mover lead:', error);
      toast.error(`Erro ao mover lead: ${error.message}`);
    },
  });
};

// NOVO: Hook para atualizar estado da IA por lead
export const useUpdateLeadAiConversationStatus = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      leadId,
      enabled
    }: {
      leadId: string;
      enabled: boolean;
    }): Promise<Lead> => {
      console.log('Atualizando status de IA para lead:', leadId, enabled);

      const { data, error } = await supabase
        .from('leads')
        .update({
          ai_conversation_enabled: enabled,
          updated_at: new Date().toISOString(),
        })
        .eq('id', leadId)
        .select()
        .single();

      if (error) {
        console.error('Erro ao atualizar status de IA:', error);
        throw new Error(`Erro ao atualizar IA: ${error.message}`);
      }

      console.log('Status de IA atualizado com sucesso:', data);

      // Mapear dados para incluir campos de compatibilidade
      return {
        ...data,
        etapa_id: data.etapa_kanban_id,
        avatar_url: data.avatar_url || null,
        nome_clinica: data.nome_clinica || null,
        ad_name: data.ad_name || null
      };
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['leads'] });
      toast.success(
        variables.enabled
          ? 'IA ativada para este lead'
          : 'IA desativada para este lead'
      );
    },
    onError: (error: Error) => {
      console.error('Erro ao atualizar IA:', error);
      toast.error(`Erro ao alterar IA: ${error.message}`);
    },
  });
};

// NOVO: Hook para pausar/despausar follow-up de um lead
export const useToggleLeadFollowup = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      leadId,
      pausado
    }: {
      leadId: string;
      pausado: boolean;
    }): Promise<Lead> => {
      console.log('Alterando status de follow-up do lead:', leadId, pausado);

      const { data, error } = await supabase
        .from('leads')
        .update({
          follow_up_pausado: pausado,
          updated_at: new Date().toISOString(),
        })
        .eq('id', leadId)
        .select()
        .single();

      if (error) {
        console.error('Erro ao alterar follow-up:', error);
        throw new Error(`Erro ao alterar follow-up: ${error.message}`);
      }

      console.log('Follow-up do lead alterado com sucesso:', data);

      // Mapear dados para incluir campos de compatibilidade
      return {
        ...data,
        etapa_id: data.etapa_kanban_id,
        avatar_url: data.avatar_url || null,
        nome_clinica: data.nome_clinica || null,
        ad_name: data.ad_name || null
      };
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['leads'] });
      toast.success(
        variables.pausado
          ? 'Follow-up pausado para este lead'
          : 'Follow-up reativado para este lead'
      );
    },
    onError: (error: Error) => {
      console.error('Erro ao alterar follow-up:', error);
      toast.error(`Erro ao alterar follow-up: ${error.message}`);
    },
  });
};
